import { request } from "./client";
import type { StaffAuth } from "./teaching";

/**
 * E1, E5, E6 and S1 — the school tier's reads and its two writes.
 *
 * 🔴 EVERY ONE SENDS `context: 'user'`. Staff login exists (§10.0), so unlike
 * the teacher tier these can genuinely load — but only with an admin token, and
 * the module stores nothing: the caller supplies it, exactly as `teaching.ts`
 * and `parent.ts` do.
 *
 * ⚠️ THERE IS NO FIXTURE IN THIS MODULE OR BESIDE ANY SCREEN THAT USES IT. The
 * §8.4 warning is the sharpest line in the TODO — *"the predecessor rendered 8
 * hardcoded children while working endpoints sat uncalled"* — and a screen that
 * takes its rows as a prop with sample data next to it is the same failure one
 * level up.
 */

export type AgeBand = "BAND_3_4" | "BAND_4_5" | "BAND_5_6";

export interface SchoolClassRow {
  classId: string;
  name: string;
  ageBand: AgeBand;
  academicYear: string | null;
  archivedAt: string | null;
  teacherName: string | null;
  childCount: number;
  activeThisWeek: number;
  sessionsThisWeek: number;
}

export interface SchoolSkillRow {
  skill: string;
  events: number;
  /** Children with at least one event in this skill — the row's denominator. */
  children: number;
}

/**
 * 🔴 COUNTS AND THEIR DENOMINATORS, NEVER A PERCENTAGE.
 *
 * OD-39/OD-72. The server does not send a ratio and the screen does not compute
 * one: *"12 of 40 children played this week"* is a fact a head teacher can act
 * on; *"30% engagement"* is a grade whose meaning depends entirely on what it
 * was divided by.
 */
export interface SchoolOverview {
  schoolId: string;
  schoolName: string;
  academicYearLabel: string | null;
  childCount: number;
  classCount: number;
  archivedClassCount: number;
  teacherCount: number;
  /*
   * ⚠️ THE NAMES SAY "THIS WEEK" AND THE WINDOW IS WHATEVER WAS ASKED FOR. They
   * are the server's field names, spelled the same way on `SchoolClassRow` and
   * at the class tier; the three `window*` fields below are what actually state
   * the period, and every caption on E1 is drawn from them rather than from a
   * field name.
   */
  activeThisWeek: number;
  sessionsThisWeek: number;
  windowDays: number;
  /**
   * 🔴 THE RANGE THE SERVER ACTUALLY MEASURED, WHICH IS NOT ALWAYS THE ONE
   * REQUESTED — the resolution rules on `SchoolWindow` say when they differ.
   * A screen that printed the dates its own inputs hold would, on those
   * occasions, put the wrong period over the right numbers.
   */
  windowFrom: string;
  windowTo: string;
  /** Children with no class at all — invisible to every scoped query. */
  childrenWithNoClass: number;
  skills: SchoolSkillRow[];
  childrenWithAnyEvents: number;
  classes: SchoolClassRow[];
}

/** The same shape `teaching.ts` uses — one NCF claim, read at two scopes. */
export interface NcfCoverage {
  rows: {
    game: string;
    category: string;
    codes: string[];
    events: number;
    children: number;
  }[];
  codesCovered: string[];
  codesUncovered: string[];
}

export interface SchoolSettings {
  schoolId: string;
  name: string;
  status: string;
  region: string;
  /**
   * 🔴 S4, LAYER 3 OF THE RECORDING CHAIN — AND IT IS RESTRICT-ONLY.
   *
   * `false` stops anything being retained school-wide. `true` means "no
   * opinion" and never enables recording for a child whose parent has not
   * consented (settings-and-controls §2.2). The copy beside the toggle has to
   * say both halves.
   */
  recordingDefault: boolean;
  defaultLanguage: string;
  academicYearLabel: string | null;
  /* ── S13 school profile (MASTER-PLAN P2-33). Set by Kalqy at onboarding;
     the admin keeps them current. `name`, `city` and `region` stay Kalqy's. ── */
  city: string | null;
  board: string | null;
  pincode: string | null;
  address: string | null;
  officeEmail: string | null;
  /** E.164 as stored, e.g. "+919812345678". */
  officePhone: string | null;
  /** CR-MONEY-5 — "TAN (for TDS)", only for a school that deducts TDS; e.g. "BLRK12345E". */
  tan: string | null;
  /* ── S13 notifications (P3-24) and the session limit (P3-25, D3). ── */
  /** School-wide switch: parents who opted in get WhatsApp updates. */
  parentWhatsAppEnabled: boolean;
  /** "Alert me when seats are nearly full · fewer than N seats". */
  seatsLowThreshold: number;
  /** T3 — a sitting ends by itself after this many minutes; null = no limit. */
  sessionMinutes: number | null;
  /**
   * 🔴 THE SIGNED-IN PERSON'S OWN EMAILS, NOT A SCHOOL SETTING. A principal may
   * change these (`saveMyNotifications`) even though every school setting is
   * read-only to them — it is their inbox, not the school's record.
   */
  notifications: { weeklyReport: boolean; seatsLow: boolean };
}

/** The S13 board choices, in the order the select lists them. */
export const SCHOOL_BOARDS = [
  "CBSE",
  "ICSE",
  "State board",
  "IB",
  "Cambridge (IGCSE)",
  "Montessori / play school",
  "Other",
] as const;

/** `sessionMinutes` bounds (D3): 5–60, or null for no limit. */
export const SESSION_MINUTES_MIN = 5;
export const SESSION_MINUTES_MAX = 60;
/** `seatsLowThreshold` bounds. 0 turns the alert off for the whole school. */
export const SEATS_LOW_MAX = 500;

export interface SchoolSettingsPatch {
  recordingDefault?: boolean;
  defaultLanguage?: "en";
  academicYearLabel?: string | null;
  /** Free text up to 60 characters; the screen offers `SCHOOL_BOARDS`. */
  board?: string | null;
  /** Up to 300 characters, newlines allowed (a postal address). */
  address?: string | null;
  officeEmail?: string | null;
  /** A 10-digit Indian number in any common format; stored as E.164. */
  officePhone?: string | null;
  /** Four letters, five digits, one letter; upper-cased by the server. `null` clears it. */
  tan?: string | null;
  parentWhatsAppEnabled?: boolean;
  seatsLowThreshold?: number;
  sessionMinutes?: number | null;
}

/**
 * `PUT /school/notifications/me` — the signed-in admin's or principal's own
 * emails. Only the keys sent change; the answer is the full pair.
 */
export const saveMyNotifications = (
  body: { weeklyReport?: boolean; seatsLow?: boolean },
  auth: StaffAuth = {},
): Promise<{ weeklyReport: boolean; seatsLow: boolean }> =>
  request<{ weeklyReport: boolean; seatsLow: boolean }>("/school/notifications/me", {
    method: "PUT",
    body,
    context: "user",
    token: auth.token,
    signal: auth.signal,
  });

/* ─────────────────────────────────────────────────────────────── S1 ── */

export type MoveKind = "PROMOTE" | "GRADUATE";

export interface RolloverInstruction {
  fromClassId: string;
  toClassId: string;
  kind: MoveKind;
}

export interface RolloverRequest {
  /**
   * 🔴 BOTH YEARS ARE NAMED BY THE CALLER. A relative band shift is not
   * idempotent, so neither year may be inferred from a clock — the safety of
   * this operation rests on the server's unique key on the year the caller
   * chose.
   */
  fromYear: string;
  toYear: string;
  instructions: RolloverInstruction[];
}

export interface PlannedMove {
  playerId: string;
  name: string;
  rollNumber: string | null;
  fromClassId: string;
  toClassId: string;
  fromBand: AgeBand;
  toBand: AgeBand;
  kind: MoveKind;
}

export interface PlanConflict {
  kind: string;
  message: string;
  fromClassId?: string;
  toClassId?: string;
  playerId?: string;
}

export interface RolloverPlan {
  fromYear: string;
  toYear: string;
  moves: PlannedMove[];
  archiveClassIds: string[];
  conflicts: PlanConflict[];
  counts: {
    childrenPromoted: number;
    childrenGraduated: number;
    classesArchived: number;
    conflicts: number;
  };
  /** One conflict blocks the whole cohort — a bulk promote is all-or-nothing. */
  runnable: boolean;
  alreadyCompleted: boolean;
}

export type RolloverStatus = "PREVIEW" | "APPLIED" | "ALREADY_RAN" | "BLOCKED";

export interface RolloverResult {
  status: RolloverStatus;
  plan: RolloverPlan;
  applied: { childrenMoved: number; classesArchived: number } | null;
}

export interface RolloverRecord {
  id: string;
  fromYear: string;
  toYear: string;
  startedAt: string;
  completedAt: string | null;
  counts: {
    childrenPromoted: number;
    childrenGraduated: number;
    classesArchived: number;
    classesEmptied: number;
  };
}

/* ───────────────────────────────────────────────────────────── calls ── */

const get = <T>(path: string, auth: StaffAuth): Promise<T> =>
  request<T>(path, { context: "user", token: auth.token, signal: auth.signal });

const post = <T>(path: string, body: unknown, auth: StaffAuth): Promise<T> =>
  request<T>(path, {
    method: "POST",
    body,
    context: "user",
    token: auth.token,
    signal: auth.signal,
  });

/**
 * The period a windowed read is measured over — `YYYY-MM-DD`, both ends
 * inclusive.
 *
 * ⚠️ THE SERVER RESOLVES IT RATHER THAN VALIDATING IT, so what comes back is
 * not always what went out: a backwards pair is swapped, an end in the future
 * is pulled to today, and a span over a year has its start moved forward. Every
 * payload that takes one of these answers with `windowFrom`/`windowTo`/
 * `windowDays`, and a screen must label itself from THOSE — see `SchoolOverview`.
 */
export interface SchoolWindow {
  from: string;
  to: string;
}

/** `?from=&to=`, or nothing at all when the caller wants the server's default. */
const windowQuery = (window?: SchoolWindow): string =>
  window === undefined
    ? ""
    : `?from=${encodeURIComponent(window.from)}&to=${encodeURIComponent(window.to)}`;

/**
 * ⚠️ THE WINDOW IS OPTIONAL AND NARROWS ONLY THE PLAY COUNTERS.
 * `activeThisWeek`, `sessionsThisWeek` and the same pair on every class row
 * follow it; `childCount`, `classCount`, `archivedClassCount` and
 * `teacherCount` are the school as it stands today and do not — a school does
 * not have fewer children because you asked about February. `SchoolReports`
 * passes nothing and keeps the server default.
 */
export const getSchoolOverview = (
  auth: StaffAuth = {},
  window?: SchoolWindow,
): Promise<SchoolOverview> =>
  get(`/school/overview${windowQuery(window)}`, auth);

/**
 * One day of the school's play.
 *
 * ⚠️ IDENTICAL TO `ActivityPoint` IN `api/teaching`, AND THE DUPLICATION IS
 * DELIBERATE. Each tier's module describes the payload that tier receives; a
 * shared alias would make the school's chart break when the class route's shape
 * changed, which is a coupling neither endpoint has. The drawing that consumes
 * both — `ActivityLineChart` — is structural for exactly this reason.
 */
export interface SchoolActivityPoint {
  /** `YYYY-MM-DD`, UTC. */
  day: string;
  /** Sittings that started on this day. */
  sessions: number;
  /** Distinct children who had at least one sitting — NOT the same as `sessions`. */
  children: number;
}

export interface SchoolActivity {
  schoolId: string;
  generatedAt: string;
  /**
   * 🔴 THE WINDOW THE SERVER ACTUALLY USED, WHICH IS NOT ALWAYS THE ONE ASKED
   * FOR. `days` is clamped to 7–90 server-side, so a chart that labelled itself
   * from its own request would mislabel the day the clamp bites. Draw the hint
   * from this field.
   */
  windowDays: number;
  /** The resolved range, `YYYY-MM-DD`, both ends inclusive. */
  windowFrom: string;
  windowTo: string;
  /** What a point counts, in words. */
  basis: string;
  points: SchoolActivityPoint[];
}

/**
 * The school's sittings, day by day.
 *
 * 🔴 THE READ `getSchoolOverview` CANNOT DO. That payload carries
 * `activeThisWeek` and `sessionsThisWeek` as two scalars over a fixed window,
 * which cannot tell a school that has never started from one that stopped a
 * fortnight ago.
 *
 * ⚠️ ITS OWN REQUEST, NOT FOLDED INTO THE OVERVIEW. A ninety-day series is a
 * heavier read than five counters, and the tiles a head teacher looks at first
 * must not wait on it — the same split the teacher tier's `ClassActivityPanel`
 * makes for the same reason.
 */
/**
 * ⚠️ IT TAKES DATES NOW, NOT A DAY COUNT. `?days=` still works server-side for
 * anything that shipped against the old shape, but E1 sends the same
 * `from`/`to` it sends the overview — one range, two routes, so the total over
 * the chart and the chart itself cannot describe different periods.
 */
export const getSchoolActivity = (
  window: SchoolWindow,
  auth: StaffAuth = {},
): Promise<SchoolActivity> =>
  get(`/school/activity${windowQuery(window)}`, auth);

/**
 * How many families have answered, school-wide.
 *
 * 🔴 COUNTS, AND THE SCREEN OWES THEM A SENTENCE. `GET /school/overview`
 * deliberately does not carry consent, because *"a bare number would lose the
 * sentence"* — the one that says a child with no consent can still play
 * everything and simply will not appear in any report. This route exists
 * because in a school of eighteen classes the only other way to learn that
 * twenty-two families have not answered was to open eighteen screens and add
 * up, which nobody does.
 *
 * ⚠️ SO THE TILE THAT DRAWS THIS MUST LINK TO THE PER-CLASS CONSENT SCREEN AND
 * CARRY THE CONSEQUENCE LINE. Counts alone are not the honest half.
 */
export interface SchoolConsent {
  children: number;
  granted: number;
  pending: number;
  revoked: number;
  expired: number;
}

export const getSchoolConsent = (
  auth: StaffAuth = {},
): Promise<SchoolConsent> => get("/school/consent", auth);

export const getSchoolCoverage = (auth: StaffAuth = {}): Promise<NcfCoverage> =>
  get("/school/coverage", auth);

/**
 * ⚠️ A PATH, NOT A FETCH — and it is the one call that cannot send an
 * Authorization header, for `coverageCsvPath`'s reason: a plain link carries
 * cookies and this API uses bearer tokens. Exported for the day a signed
 * download URL is worth building; E5 fetches the JSON and builds the file
 * itself in the meantime.
 */
export const schoolCoverageCsvPath = (): string => "/school/coverage.csv";

export const getSchoolSettings = (
  auth: StaffAuth = {},
): Promise<SchoolSettings> => get("/school/settings", auth);

/**
 * ⚠️ POST rather than PATCH because `client.ts` types `method` as
 * `'GET' | 'POST'`, and that file belongs to another slice. The body is still
 * PATCH-shaped: only the keys present are written, and the server rejects
 * unknown ones.
 */
export const saveSchoolSettings = (
  patch: SchoolSettingsPatch,
  auth: StaffAuth = {},
): Promise<SchoolSettings> => post("/school/settings", patch, auth);

/**
 * 🔴 THE PREVIEW AND THE COMMIT ARE TWO PATHS INTO ONE SERVER FUNCTION.
 *
 * `planRollover` and `runRollover` send the identical body; the server plans
 * both times and only the second one writes. What the admin confirms is
 * therefore exactly what runs — the `roster` preview's rule, applied to the one
 * screen in the product with no undo.
 */
export const planRollover = (
  body: RolloverRequest,
  auth: StaffAuth = {},
): Promise<RolloverResult> => post("/school/rollover/plan", body, auth);

export const runRollover = (
  body: RolloverRequest,
  auth: StaffAuth = {},
): Promise<RolloverResult> => post("/school/rollover", body, auth);

export const getRollovers = (auth: StaffAuth = {}): Promise<RolloverRecord[]> =>
  get("/school/rollover", auth);

/* ─────────────────────────────────────────────────────── holidays ── */

export interface HolidayRow {
  id: string;
  /** `YYYY-MM-DD`. */
  day: string;
  name: string;
}

/**
 * The days the school is closed.
 *
 * 🔴 ONE ROW PER DAY, AND THE SCREEN GROUPS THEM BACK. The server stores a day
 * at a time so two records cannot disagree about one date (`Holiday` in
 * org.prisma); "Onam · 2–6 Sep" is a reading of five rows, computed by
 * `holidayRanges.ts` and never stored.
 */
export const getHolidays = (auth: StaffAuth = {}): Promise<HolidayRow[]> =>
  get("/school/holidays", auth);

/** Both ends inclusive. The server expands the range and caps it at 60 days. */
export const addHolidays = (
  body: { from: string; to: string; name: string },
  auth: StaffAuth = {},
): Promise<HolidayRow[]> => post("/school/holidays", body, auth);

/**
 * ⚠️ A POST, NOT A DELETE — `client.ts` types `method` as `'GET' | 'POST'`, so a
 * DELETE here would be a route nothing could call.
 */
export const removeHoliday = (
  id: string,
  auth: StaffAuth = {},
): Promise<{ ok: true }> => post(`/school/holidays/${id}/remove`, {}, auth);

/* ─────────────────────────────────────────────────────────── the play log ── */

/**
 * One sitting, one game.
 *
 * 🔴 THE GRAIN IS (session × game) AND THE SERVER DECIDES IT, NOT THIS FILE.
 * `Session` in the schema carries no game column — `Event` does — so a sitting
 * that spans two games is two rows here. A screen that assumed one row per
 * sitting would silently drop the second game.
 *
 * 🔴 THERE IS NO POINTS COLUMN IN THIS PRODUCT AND THIS TYPE DOES NOT PRETEND
 * OTHERWISE. `rounds`, `correct`/`wrong` and `highestLevel` are what the event
 * stream can actually answer; `Progress.coins` is a lifetime total with no game
 * attached and `RoundOutcome` is anonymous telemetry with no child on it.
 */
export interface GameLogRow {
  sessionId: string;
  /** ISO. When the sitting began. */
  playedAt: string;
  endedAt: string | null;
  /** ISO. The last event recorded for THIS game in the sitting. */
  lastEventAt: string | null;
  playerId: string;
  studentName: string;
  rollNumber: string | null;
  classId: string | null;
  className: string | null;
  teacherId: string | null;
  /** 🔴 NULL MEANS PLAYED OUTSIDE A CLASS — never "unknown teacher". */
  teacherName: string | null;
  game: string;
  /** Levels FINISHED. Not levels opened. */
  rounds: number;
  correct: number;
  wrong: number;
  /** Read from finished rounds only, so an attempt cannot promote a child. */
  highestLevel: number | null;
}

export interface GameLogTeacher {
  teacherId: string;
  name: string;
}

export interface SchoolGameLog {
  schoolId: string;
  generatedAt: string;
  windowDays: number;
  windowFrom: string;
  windowTo: string;
  /** What a row means, in the server's words. The screen prints it. */
  basis: string;
  rows: GameLogRow[];
  /** 🔴 SITTINGS, NOT ROWS — rows fan out per game, so total ≤ rows.length. */
  total: number;
  page: number;
  pageSize: number;
  games: string[];
  teachers: GameLogTeacher[];
}

/** What the screen may narrow by. Every field is optional. */
export interface GameLogQuery {
  from?: string;
  to?: string;
  game?: string;
  classId?: string;
  teacherId?: string;
  page?: number;
  pageSize?: number;
}

/**
 * ⚠️ BUILT WITH `URLSearchParams` RATHER THAN TEMPLATE CONCATENATION. A class
 * name or game id is not guaranteed to be URL-safe, and `windowQuery` above
 * escapes by hand precisely because it only ever carries two ISO dates. This one
 * carries caller-chosen ids.
 *
 * ⚠️ EMPTY VALUES ARE OMITTED, NOT SENT AS BLANKS. `?game=` would reach the
 * server as an empty string, which its own narrowing treats as absent — so
 * sending it is noise in the URL a reader might bookmark.
 */
const gameLogQuery = (query: GameLogQuery): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s === "" ? "" : `?${s}`;
};

/**
 * Every sitting: which child played which game, when, and with which teacher.
 *
 * 🔴 THE ROW-LEVEL READ BEHIND THE GAME CARDS ON E1. Those fold the school to
 * one figure per game, which answers *"is Letter Lagoon being used"* and cannot
 * answer *"has Aarav played this term, and who was with him"*.
 */
export const getSchoolGameLog = (
  query: GameLogQuery = {},
  auth: StaffAuth = {},
): Promise<SchoolGameLog> =>
  get(`/school/game-log${gameLogQuery(query)}`, auth);
